import * as React from 'react';

import { HStack, VStack } from '@looko-design-system';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

import { MUIStyleSheet } from '@/utils/MUIStyleSheet';

import { FOLDED_SIDEBAR_WIDTH, SIDEBAR_WIDTH } from './Sidebar';
import { HEADER_HEIGHT } from './header';

interface MainProps {
  sidebarOpened: boolean;
  headerHeight: number;
  isHiddenSidebarSection: boolean;
}

const Main = styled('main', {
  shouldForwardProp: prop => prop !== 'sidebarOpened' && prop !== 'headerHeight' && prop !== 'isHiddenSidebarSection',
})<MainProps>(({ theme, sidebarOpened, headerHeight, isHiddenSidebarSection }) => ({
  backgroundColor: theme.palette.grey[100],
  marginTop: headerHeight,
  marginLeft: !isHiddenSidebarSection ? FOLDED_SIDEBAR_WIDTH : 0,
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  minHeight: `calc(100vh - ${headerHeight}px)`,
  width: `calc(100vw - ${!isHiddenSidebarSection ? FOLDED_SIDEBAR_WIDTH : 0}px)`,
  [theme.breakpoints.down('lg')]: {
    width: `${FOLDED_SIDEBAR_WIDTH + 1200}px`,
  },
  ...(sidebarOpened && {
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    width: `calc(100vw - ${SIDEBAR_WIDTH}px)`,
    marginLeft: SIDEBAR_WIDTH,
    [theme.breakpoints.down('lg')]: {
      width: `${SIDEBAR_WIDTH + 1200}px`,
    },
  }),
}));

/**
 * 메인 레이아웃을 구성하는 기본 틀.
 */
export const MainLayoutBase = ({
  appBar,
  sideBar,
  children,
  footer,

  sidebarOpened = true,
  headerHeight = HEADER_HEIGHT,
}: {
  appBar?: React.ReactNode;
  sideBar?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;

  sidebarOpened?: boolean;
  headerHeight?: number;
}) => {
  const isHiddenSidebarSection = sideBar == null;

  return (
    <HStack className="main-layout" sx={styles.root}>
      {sideBar}

      <VStack width={'100%'}>
        {appBar != null && <Box sx={styles.appBar}>{appBar}</Box>}

        <Main
          sidebarOpened={sidebarOpened && !isHiddenSidebarSection}
          headerHeight={headerHeight}
          isHiddenSidebarSection={isHiddenSidebarSection}
        >
          {children}
        </Main>
      </VStack>
      {footer}
    </HStack>
  );
};

const styles = MUIStyleSheet.create({
  root: {
    display: 'flex',
    height: '100%',
    width: '100%',
  },
  appBar: {
    backgroundColor: 'white',
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    position: 'fixed',
    top: 0,
    right: 0,
    left: 0,
    zIndex: 5,
  },
});
